import React, { useState, useEffect } from "react";
import { apiService, apiUtils } from "../services/apiService";
import { FaPlus, FaCode, FaStar, FaTrash } from "react-icons/fa";

const Skills = () => {
  const [formData, setFormData] = useState({
    category: "",
    skill: "",
    rank: "",
  });
  const [skills, setSkills] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      const response = await apiService.skills.getAll();
      setSkills(response.data);
    } catch (error) {
      console.error("Error fetching skills:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      category: "",
      skill: "",
      rank: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await apiUtils.handleFormSubmit(apiService.skills.create, formData, {
        successMessage: "Skill added successfully!",
        errorMessage: "Failed to add skill",
        resetForm,
      });
      fetchSkills();
    } catch (error) {
      console.error("Error adding skill:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiUtils.handleDelete(() => apiService.skills.delete(id), {
        successMessage: "Skill deleted successfully!",
        errorMessage: "Failed to delete skill",
        confirmMessage: "Are you sure you want to delete this skill?",
        onSuccess: () => fetchSkills(),
      });
    } catch (error) {
      console.error("Error deleting skill:", error);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
          <div className="flex items-center">
            <FaPlus className="text-white text-xl mr-3" />
            <h1 className="text-xl font-bold text-white">Add New Skill</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaCode className="inline mr-2 text-indigo-600" />
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                required
              >
                <option value="">Select category</option>
                <option value="web development">Web Development</option>
                <option value="software development">
                  Software Development
                </option>
                <option value="version control">Version Control</option>
                <option value="cloud operation">Cloud Operation</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaCode className="inline mr-2 text-indigo-600" />
                Skill Name
              </label>
              <input
                type="text"
                name="skill"
                value={formData.skill}
                onChange={handleChange}
                placeholder="e.g., React, Node.js"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaStar className="inline mr-2 text-indigo-600" />
                Proficiency Level
              </label>
              <select
                name="rank"
                value={formData.rank}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                required
              >
                <option value="">Select level</option>
                <option value="1">Beginner (1)</option>
                <option value="2">Elementary (2)</option>
                <option value="3">Intermediate (3)</option>
                <option value="4">Advanced (4)</option>
                <option value="5">Expert (5)</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Adding Skill...
                </>
              ) : (
                <>
                  <FaPlus className="mr-2" />
                  Add Skill
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Current Skills</h2>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-indigo-600 mx-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <p className="mt-2 text-gray-600">Loading skills...</p>
            </div>
          ) : skills.length === 0 ? (
            <div className="text-center py-8">
              <FaCode className="text-gray-400 text-4xl mx-auto mb-4" />
              <p className="text-gray-600">No skills added yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {skills.map((skill) => (
                <div
                  key={skill._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900">
                      {skill.skill}
                    </h3>
                    <button
                      onClick={() => handleDelete(skill._id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete skill"
                    >
                      <FaTrash size={14} />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{skill.category}</p>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-2">Level:</span>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <FaStar
                          key={star}
                          className={`text-sm ${
                            star <= skill.rank
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Skills;
